import ReactJsonView from '@microlink/react-json-view'
import { useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ThreeDots } from 'react-loading-icons'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Textarea } from 'components/ui/textarea'
import { Banner, Button, CollapsibleContainer } from 'slab'
import { generateMonthOptions, getNextMonthStartDate } from 'utils/dateOptions'
import {
  getGetBrokersTaskWorkflowsQueryKey,
  usePostBrokersTaskWorkflows,
} from 'utils/web_api'
import type {
  BrokersWorkflow,
  PostBrokersTaskWorkflowsTiming,
  PostBrokersTaskWorkflowsWorkflowType,
} from 'utils/web_api.schemas'

import TasksList from './TasksList'
import WorkflowDAG from './WorkflowDAG'
import {
  WORKFLOW_TYPE_LABELS,
  WORKFLOW_TYPE_OPTIONS,
} from './WorkflowsTypeFilter'

interface CreateWorkflowDialogProps {
  enrollmentId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

// Timing options
const TIMING_OPTIONS: Array<{
  value: PostBrokersTaskWorkflowsTiming
  label: string
}> = [
  { value: 'mid_year', label: 'Mid Year' },
  { value: 'year_over_year', label: 'Year Over Year' },
]

// Type for preview response - extends BrokersWorkflow with preview-specific fields

export const CreateWorkflowDialog = ({
  enrollmentId,
  isOpen,
  onClose,
  onSuccess,
}: CreateWorkflowDialogProps) => {
  const [workflowType, setWorkflowType] = useState<
    PostBrokersTaskWorkflowsWorkflowType | ''
  >('')
  const [timing, setTiming] = useState<PostBrokersTaskWorkflowsTiming | ''>('')
  const [metadata, setMetadata] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [previewData, setPreviewData] = useState<BrokersWorkflow | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const createMutation = usePostBrokersTaskWorkflows()

  // Generate date options (3 past + 3 future months)
  const { futureOptions, pastOptions } = generateMonthOptions(3, 3)
  const dateOptions = [...pastOptions.reverse(), ...futureOptions]

  // Auto-select next month's start date when dialog opens
  useEffect(() => {
    if (isOpen && !startDate) {
      setStartDate(getNextMonthStartDate())
    }
  }, [isOpen, startDate])

  const handleSubmit = async (preview: boolean) => {
    // Clear previous errors
    if (preview) {
      setPreviewError(null)
    } else {
      setCreateError(null)
    }

    // Validate required fields
    if (!workflowType) {
      toast.error('Please select a workflow type')
      return
    }

    if (!timing) {
      toast.error('Please select a timing context')
      return
    }

    if (!startDate) {
      toast.error('Please select a start date')
      return
    }

    // Parse metadata
    let parsedMetadata: Record<string, unknown> = {}
    if (metadata.trim()) {
      try {
        parsedMetadata = JSON.parse(metadata)
      } catch {
        toast.error('Invalid JSON in metadata field')
        return
      }
    }

    try {
      if (preview) {
        setIsPreviewLoading(true)
        setPreviewData(null)
      }

      const response = await createMutation.mutateAsync({
        data: {
          enrollment_id: enrollmentId,
          workflow_type: workflowType,
          timing,
          metadata:
            Object.keys(parsedMetadata).length > 0 ? parsedMetadata : undefined,
          preview,
          start_date: startDate,
        } as any, // Type assertion needed until Orval regenerates types with new fields
      })

      if (preview) {
        setPreviewData(response as BrokersWorkflow)
      } else {
        // Invalidate workflows query to refetch
        await queryClient.invalidateQueries({
          queryKey: getGetBrokersTaskWorkflowsQueryKey({
            'filters[enrollment_ids][]': [enrollmentId],
          }),
        })

        toast.success('Workflow created successfully')
        onSuccess?.()
        handleClose()
      }
    } catch (error: any) {
      console.error('Failed to create workflow:', error)
      const errorMessage = error?.message || (preview ? 'Failed to generate preview' : 'Failed to create workflow')

      if (preview) {
        setPreviewError(errorMessage)
      } else {
        setCreateError(errorMessage)
      }
    } finally {
      if (preview) {
        setIsPreviewLoading(false)
      }
    }
  }

  const handlePreview = () => handleSubmit(true)
  const handleCreate = () => handleSubmit(false)

  const handleClose = () => {
    setWorkflowType('')
    setTiming('')
    setMetadata('')
    setStartDate('')
    setPreviewData(null)
    setPreviewError(null)
    setCreateError(null)
    onClose()
  }

  // Extract tasks from either preview or workflow response
  const tasks = previewData?.tasks_summary || []

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='max-w-5xl max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle>Create Workflow</DialogTitle>
          <DialogDescription>
            Configure and preview the workflow before creating it for this
            enrollment.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Form Section */}
          <div className='space-y-4'>
            {/* Workflow Type, Timing, and Start Date in a flex row */}
            <div className='flex flex-wrap gap-4'>
              <div className='flex-1 min-w-[200px] grid gap-2'>
                <Label htmlFor='workflow-type'>
                  Workflow Type <span className='text-red-500'>*</span>
                </Label>
                <Select
                  value={workflowType}
                  onValueChange={(value) =>
                    setWorkflowType(
                      value as PostBrokersTaskWorkflowsWorkflowType
                    )}
                >
                  <SelectTrigger id='workflow-type'>
                    <SelectValue placeholder='Select workflow type' />
                  </SelectTrigger>
                  <SelectContent>
                    {WORKFLOW_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='flex-1 min-w-[200px] grid gap-2'>
                <Label htmlFor='timing'>
                  Timing <span className='text-red-500'>*</span>
                </Label>
                <Select
                  value={timing}
                  onValueChange={(value) =>
                    setTiming(value as PostBrokersTaskWorkflowsTiming)}
                >
                  <SelectTrigger id='timing'>
                    <SelectValue placeholder='Select timing context' />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMING_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='flex-1 min-w-[200px] grid gap-2'>
                <Label htmlFor='start-date'>
                  Start Date <span className='text-red-500'>*</span>
                </Label>
                <Select
                  value={startDate}
                  onValueChange={(value) => setStartDate(value)}
                >
                  <SelectTrigger id='start-date'>
                    <SelectValue placeholder='Select start date' />
                  </SelectTrigger>
                  <SelectContent>
                    {dateOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className='text-sm text-gray-500'>
              Start date will automatically generate enriched metadata including
              dependent changes, plan changes, and plan comparison analysis
              using EnrollmentComparator.
            </p>

            <div className='grid gap-2'>
              <Label htmlFor='metadata'>Metadata (optional JSON)</Label>
              <Textarea
                id='metadata'
                placeholder='{"key": "value"}'
                value={metadata}
                onChange={(e) => setMetadata(e.target.value)}
                rows={3}
                className='font-mono text-sm'
              />
              {metadata &&
                (() => {
                  try {
                    JSON.parse(metadata)
                    return null
                  } catch {
                    return (
                      <p className='text-sm text-red-600'>
                        Invalid JSON format
                      </p>
                    )
                  }
                })()}
            </div>

            <div className='flex justify-end'>
              <Button
                onClick={handlePreview}
                disabled={!workflowType || !timing || !startDate || isPreviewLoading || createMutation.isPending}
                variant='primary'
              >
                {isPreviewLoading || createMutation.isPending
                  ? 'Generating Preview...'
                  : 'Preview Workflow'}
              </Button>
            </div>
          </div>

          {/* Preview Error Banner */}
          {previewError && (
            <Banner type='critical' title='Failed to generate preview'>
              {previewError}
            </Banner>
          )}

          {isPreviewLoading && (
            <div className='flex justify-center items-center py-8'>
              <ThreeDots stroke='#9C3BFF' fill='#9C3BFF' className='h-40 w-40' />
            </div>
          )}

          {/* Preview Section */}
          {previewData && (
            <div className='space-y-4'>
              <div className='border-t pt-4'>
                <h3 className='text-lg font-semibold mb-2'>Workflow Preview</h3>
                <div className='space-y-2 text-sm'>
                  <div className='flex justify-start'>
                    <span className='text-gray-600'>Type:</span>
                    <span className='font-medium'>
                      {
                        WORKFLOW_TYPE_LABELS[
                          previewData.workflow_type as PostBrokersTaskWorkflowsWorkflowType
                        ]
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Task Dependency Graph */}
              {tasks.length > 0 && (
                <div className='pt-4'>
                  <CollapsibleContainer
                    heading='Task Dependency Graph'
                    defaultOpen={false}
                  >
                    <div className='mt-4'>
                      <WorkflowDAG tasks={tasks} />
                    </div>
                  </CollapsibleContainer>
                </div>
              )}

              {/* Task List */}
              {tasks.length > 0 && (
                <TasksList
                  tasks={tasks}
                  workflowStatus={previewData.status as any}
                  showActions={false}
                />
              )}

              {/* Workflow Metadata */}
              <div className='border-t pt-4'>
                <CollapsibleContainer
                  heading='Raw Workflow Metadata'
                  defaultOpen={false}
                >
                  <div className='mt-4 p-4'>
                    {previewData.metadata &&
                    Object.keys(previewData.metadata).length > 0
                      ? (
                        <ReactJsonView
                          src={previewData.metadata as Record<string, unknown>}
                          theme={'github' as any}
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          enableClipboard={false}
                        />
                        )
                      : (
                        <p className='text-sm text-gray-500'>
                          No workflow metadata is currently stored for this
                          workflow.
                        </p>
                        )}
                  </div>
                </CollapsibleContainer>
              </div>

              <Banner type='info' title='Review the workflow preview above'>
                When ready, click "Create Workflow" to create a new workflow for
                this enrollment.
              </Banner>
            </div>
          )}
        </div>

        {previewData && (
          <>
            {/* Create Error Banner */}
            {createError && (
              <Banner type='critical' title='Failed to create workflow'>
                {createError}
              </Banner>
            )}

            <DialogFooter>
              <Button
                onClick={handleClose}
                variant='secondary'
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>

              <Button
                onClick={handleCreate}
                variant='primary'
                disabled={createMutation.isPending || !workflowType || !previewData}
                leftIcon={Check}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Workflow'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CreateWorkflowDialog
